import React, { Component, useEffect } from 'react'
import { connect } from 'react-redux'
import moment from 'moment-timezone'
import { setShowIntercom } from 'actions'
import { languages, mapLanguageToComponentProps } from 'i18n'
import NavigationBar from 'components/navigation-bar'
import Error410 from 'components/errors/410'
import Error404 from 'components/errors/404'
import Unpublished from 'careers/components/unpublished'
import UrlLanguage from 'components/url-language'
import ThemeProvider from 'theme/ThemeProvider'
import { getPrimaryColors } from 'theme/branding/custom-colors'
import { PrimaryLink } from 'components/styled/typography'
import { AppContent } from 'components/styled/page/app-content'
import CandidateFooter from 'components/candidate-footer'
import { BrandingType } from 'types'
import { I18nComponentType } from 'i18n-types'

interface CareersAppProps {
  params: {
    language: string
    companySlug?: string
    adSlug?: string
  }
  httpError?: number
  company: {
    branding: BrandingType
  }
  children?: any // todo figure out what type this should be? 
  unpublished?: boolean
  dispatch: any
  i18n: I18nComponentType
}

const mapStateToProps = (state: any) => ({
  httpError: state.global.httpError,
  company: state.careers.company,
  unpublished: state.careers.unpublished,
})

export function CareersApp({
  i18n,
  params,
  httpError,
  company,
  children,
  unpublished = false,
  dispatch,
}: CareersAppProps) {
  const { branding } = company
  const { referrer } = document

  useEffect(() => {
    dispatch(setShowIntercom())
  }, [])

  useEffect(() => {
    const dir = params.language === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.dir = dir
  }, [params.language])

  const renderError = (link: string) => {
    if (httpError === 410) {
      return (
        <Error410>
          <PrimaryLink to={link}>{i18n.error410.link}</PrimaryLink>
        </Error410>
      )
    }
    if (httpError === 404) {
      return <Error404 />
    }

    if (unpublished) {
      return (
        <Unpublished>
          <PrimaryLink to={link}>{i18n.error404.link}</PrimaryLink>
        </Unpublished>
      )
    }
    return children
  }

  if (
    referrer &&
    document.cookie.indexOf('referrer=') < 0 &&
    !referrer.includes(
      'https://www.linkedin.com/uas/login?session_redirect=%2Foauth'
    )
  ) {
    document.cookie = `referrer=${referrer}; expires=${moment()
      .add(7, 'days')
      .utc()
      .toString()}`
  }
  const link = `/careers/${params.language}/${params.companySlug}`

  const {
    headerFont,
    bodyFont,
    backgroundColor,
    textColor,
    brandColor,
    buttonTextColor,
    headerTextColor,
  } = branding ?? {}

  const primaryColors = brandColor ? getPrimaryColors(brandColor) : undefined
  const customTheme = branding
    ? {
        color: {
          ...primaryColors,
          headerText: headerTextColor,
          bodyText: textColor,
          primaryButtonText: buttonTextColor,
          background: backgroundColor,
        },
        font: {
          primary: bodyFont,
          header: headerFont,
        },
      }
    : undefined

  return (
    <ThemeProvider customTheme={customTheme} useGlobalStyle customizeLink>
      <AppContent footer={<CandidateFooter />}>
        <UrlLanguage params={params} languages={languages} />
        {params.adSlug && (
          <NavigationBar
            routePrefix={link}
            className="navigation-bar careers"
            language={params.language}
          />
        )}
        {renderError(link)}
      </AppContent>
    </ThemeProvider>
  )
}

export default connect(mapLanguageToComponentProps(mapStateToProps))(CareersApp)
